import Axios, { AxiosError } from 'axios';
import { USE_API_MOCKS } from '../env';
import { AxiosCacheInstance, setupCache } from 'axios-cache-interceptor';
import MockAdapter from 'axios-mock-adapter';
import { jwtDecode } from 'jwt-decode';
import { routesPath } from '@router/routes';
import { LocalStorage, SessionStorage } from 'src/utils/storage';
import { activeMocks, API_URL_VALUES_TYPE } from 'src/activeMocks';

const callToast = (error: AxiosError) => {
  if (error.response) {
    if (error.response.status === 401) {
      Object.keys(LocalStorage).forEach(key => {
        if (/@@auth0/.test(key)) {
          LocalStorage.removeItem(key);
          SessionStorage.clear();
          document.cookie = '';
        }
      });
      window.location.reload();
      return;
    }
  } else if (error.request) {
    console.error(error.request);
  } else {
    console.error('Error', error.message);
  }

  return Promise.reject(error);
};

export const RESTClientInstance = Axios.create({
  baseURL: '/api',
  withCredentials: false,
});

const controller = new AbortController();

RESTClientInstance.interceptors.request.use(config => {
  const token = sessionStorage.getItem('idToken');

  if (token) {
    const { exp } = jwtDecode(token) ?? {};
    if (!exp) {
      throw new Error('Token does not contain an expiration time.');
    }

    const expirationTime = exp * 1000;

    const isExpired = expirationTime <= (Date.now() - 30000);

    if (isExpired) {
      controller.abort();
      sessionStorage.clear();
      window.location.href = routesPath.logout;
      return Promise.reject(new Error('Token has expired'));
    } else {
      config.headers.Authorization = `Bearer ${token}`;
      config.signal = config.signal ?? controller.signal;
    }
  }
  return config;
});

RESTClientInstance.interceptors.response.use(response => response, callToast);

export const RESTClientInstanceMock = Axios.create({
  baseURL: '/api',
  withCredentials: false,
});

RESTClientInstanceMock.interceptors.response.use(config => {
  if (!USE_API_MOCKS) return config;
  console.groupCollapsed(
    `AXIOS MOCK - [${config.config.method?.toUpperCase()}]: ${
      config.config.url
    }`
  );

  console.log('Request - Params', config.config.params);
  console.log('Request - Body', config.config.data);
  console.log('Request - Headers', config.config.headers);

  console.log('Response', config.data);
  console.groupCollapsed('Call Stack');
  console.trace();
  console.groupEnd();

  console.groupEnd();

  return config;
});

export const RESTClient = setupCache(RESTClientInstance);
export const RESTClientMock = RESTClientInstanceMock as AxiosCacheInstance;

export const hasUrlMock = (
  mockApiUrl?: API_URL_VALUES_TYPE,
  mockForce?: boolean
) => {
  return mockForce || USE_API_MOCKS || (mockApiUrl && activeMocks[mockApiUrl]);
};

/**
 * Get the client based passed parameters to use mock ot real API
 * @param mockApiUrl
 * @param mockForce
 * @returns RESTClient | RESTClientMock
 */
export const getClient = (
  mockApiUrl?: API_URL_VALUES_TYPE,
  mockForce?: boolean
) => {
  return hasUrlMock(mockApiUrl, mockForce) ? RESTClientMock : RESTClient;
};

export const axiosMock = new MockAdapter(RESTClientMock, {
  delayResponse: 1000,
});
